import { FC, useContext, useEffect, useRef, useState } from "react";

import { Box, Stack, Slide, useTheme, Fade } from "@mui/material";
import { FormBackgroundTheme } from "@simplyk/common";

import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { ShapesProvider } from "@/canvas/components/BackgroundShapes/ShapesContext";
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

const ZEFFY_URL = "https://www.zeffy.com";
const MOBILE_HEADER_HEIGHT = 44;
const MOBILE_HEADER_HEIGHT_WITH_ACTION = 54;

type FormLayoutProps = {
  step: FormStepKey;
  slots: {
    thankYou?: React.ReactNode;
    languageSelect: React.ReactNode;
    closeEmbedButton: React.ReactNode;
    headerAction?: React.ReactNode;
    selectionStep: React.ReactNode;
    addOnsStep?: React.ReactNode;
    paymentStep: React.ReactNode;
    archivedFormBanner?: React.ReactNode;
    pushMonthlyStep?: React.ReactNode;
  };
  backgroundTheme?: FormBackgroundTheme | null;
  brandColorHex?: string;
  isEmbed?: boolean;
  preloadSteps?: boolean;
  animateTransitions?: boolean;
};

export const FormLayout: FC<FormLayoutProps> = ({
  step,
  slots,
  preloadSteps = false,
  animateTransitions = true,
  backgroundTheme = null,
  brandColorHex,
  isEmbed = false,
}) => {
  const theme = useTheme();
  const { isPreview } = usePreviewContext();

  const { dafFlow } = useContext(FrontendFormContext);
  const isDafFlow = step === "payment" && Boolean(dafFlow);
  const hasHeaderAction = Boolean(slots.headerAction) && step !== "pushMonthly" && !isDafFlow;

  return (
    <>
      {slots.thankYou && (
        <Box
          sx={{
            position: "fixed",
            display: "flex",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
          }}
        >
          {slots.thankYou}
        </Box>
      )}
      <ShapesProvider backgroundTheme={backgroundTheme} brandColorHex={brandColorHex}>
        <Stack
          direction="column"
          sx={{
            position: "relative",
            flexGrow: 1,
          }}
        >
          {isPreview && <Box sx={{ paddingTop: 2 }} />}

          {!isEmbed && !isPreview && !isDafFlow && (
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                height: hasHeaderAction ? MOBILE_HEADER_HEIGHT_WITH_ACTION : MOBILE_HEADER_HEIGHT,
                paddingInline: 2,
                flexShrink: 0,
              }}
            >
              <ThemedZeffyLogo href={ZEFFY_URL} width={66} height={20} />
            </Box>
          )}
          {slots.archivedFormBanner && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 99,
                padding: { xs: theme.spacing(2), md: theme.spacing(3) },
                display: "flex",
                justifyContent: "center",
              }}
            >
              {slots.archivedFormBanner}
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              top: { xs: hasHeaderAction ? theme.spacing(1.5) : theme.spacing(1), md: theme.spacing(3) },
              right: { xs: theme.spacing(2), md: theme.spacing(4) },
              zIndex: 100,
            }}
          >
            <Stack direction="row" gap={2} alignItems="center">
              {step !== "pushMonthly" && !isDafFlow && slots.headerAction}
              {step !== "pushMonthly" && !isDafFlow && slots.languageSelect}
              {slots.closeEmbedButton}
            </Stack>
          </Box>

          <Steps step={step} slots={slots} preloadSteps={preloadSteps} animateTransitions={animateTransitions} />
        </Stack>
      </ShapesProvider>
    </>
  );
};

const Steps: FC<FormLayoutProps> = ({ step, slots, preloadSteps = false, animateTransitions = true }) => {
  const [isClientReady, setIsClientReady] = useState(false);
  const topAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClientReady(true);
  }, []);

  useEffect(() => {
    if (step === "payment" || step === "addOns") {
      topAnchorRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }
  }, [step]);

  const shouldRenderPaymentStep = step === "payment" || (preloadSteps && isClientReady);

  return (
    <>
      <div ref={topAnchorRef} />
      {/* Step 1 */}
      {/* appear=false: skip enter animation on first paint (SSR/hydration). Enter still runs when `in` goes false→true (e.g. payment → selection). */}
      <Fade
        appear={animateTransitions}
        in={step === "selection"}
        timeout={animateTransitions ? 500 : 0}
        style={{ display: step === "selection" ? "flex" : "none", flexDirection: "column" }}
      >
        <Box sx={{ height: "100%", flexGrow: 1 }}>{step === "selection" && slots.selectionStep}</Box>
      </Fade>

      <Box
        width="100%"
        minHeight="100%"
        sx={{ overflowX: "clip", display: step === "addOns" ? "flex" : "none", flexDirection: "column" }}
      >
        <Slide
          appear={animateTransitions}
          direction="left"
          in={step === "addOns"}
          mountOnEnter
          unmountOnExit={false}
          timeout={animateTransitions ? 500 : 0}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100%" }}>
            {step === "addOns" && slots.addOnsStep}
          </Box>
        </Slide>
      </Box>

      <Box
        width="100%"
        minHeight="100%"
        sx={{ overflowX: "hidden", display: step === "pushMonthly" ? "flex" : "none", flexDirection: "column" }}
      >
        <Slide
          appear={animateTransitions}
          direction="left"
          in={step === "pushMonthly"}
          mountOnEnter
          unmountOnExit={false}
          timeout={500}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100%" }}>
            {step === "pushMonthly" && slots.pushMonthlyStep}
          </Box>
        </Slide>
      </Box>

      {/* Step 2 */}
      <Box width="100%" minHeight="100%" sx={{ overflowX: "hidden", display: step === "payment" ? "block" : "none" }}>
        <Slide
          appear={animateTransitions}
          direction="left"
          in={step === "payment"}
          mountOnEnter={!preloadSteps} // If not preview, we preload Step 2 for Smooth Transitions
          unmountOnExit={false}
          timeout={500}
        >
          <Box>{shouldRenderPaymentStep && slots.paymentStep}</Box>
        </Slide>
      </Box>
    </>
  );
};
